import { Input } from "@Light/components/system/Input";
import { ShieldTick } from "@untitledui/icons";
import { useState } from "react";

const MASKED_SSN = "•••-••-••••";

function formatSsn(value: string): string {
  const digits = value.replace(/\D/g, "").slice(0, 9);
  if (digits.length > 5) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
  }
  if (digits.length > 3) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return digits;
}

export function isCompleteSsn(value: string): boolean {
  return value.replace(/\D/g, "").length === 9;
}

function maskDigits(value: string): string {
  return value.replace(/\d/g, "\u2022");
}

export type SsnInputProps = {
  /** Whether a number is already on file, which an empty field keeps. */
  hasStoredSsn?: boolean;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  isError?: boolean;
};

export const SsnInput: React.FC<SsnInputProps> = ({
  hasStoredSsn,
  value,
  onChange,
  onBlur,
  isError,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  // Digits are only ever on screen while they're being typed. A blurred field
  // masks what's there, or stands in for the number already on file. Masking
  // here rather than with type="password" keeps Chrome from treating this as a
  // login form and offering to save a password.
  const masked = value ? maskDigits(value) : hasStoredSsn ? MASKED_SSN : "";

  return (
    <Input
      type="text"
      icon={ShieldTick}
      // Disable Inter's contextual alternates to prevent middots after dashes from misaligning
      className="font-features-['calt'_0]"
      value={isFocused ? value : masked}
      placeholder="XXX-XX-XXXX"
      maxLength={11}
      inputMode="numeric"
      autoComplete="off"
      isError={isError}
      onChange={(e) => {
        onChange(formatSsn(e.target.value));
      }}
      onFocus={() => {
        setIsFocused(true);
      }}
      onBlur={() => {
        setIsFocused(false);
        onBlur?.();
      }}
      onPaste={(e) => {
        e.preventDefault();
        onChange(formatSsn(e.clipboardData.getData("Text")));
      }}
      data-testid="ssn-input"
    />
  );
};
